import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InnerImageZoom from 'react-inner-image-zoom';

import StandardModal from 'components/consumer/StandardModal';
import PDPShownIn from 'components/consumer/PDP/PDPShownIn';
import useKeyboardControlForIndexedItems from 'hooks/useKeyboardControlForIndexedItems';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import Prefetcher from 'components/essentials/Prefetcher';
import IconArrowRightSimple from 'components/icons/iconArrowRightSimple';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPDesktopFullScreenGallery2Thumbnail from './components/PDPDesktopFullScreenGallery2Thumbnail';

import usePhotoWidth from './usePhotoWidth';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';

const PDPDesktopFullScreenGallery2UI = ({
  isEditOrder = false,
  enableCrops,
  handleVideoSelected = () => {},
  introVideoId = '',
  isOpen,
  isVideoSelected = false,
  onClose,
  photos = [],
  photoSrc = {},
  photosToPreload = [],
  productName,
  selectedPhotoIndex,
  setSelectedPhotoIndex,
  videoThumbnailUrl,
}) => {
  const thumbnailWrapperRef = useRef(null);
  const selectedThumbnailRef = useRef(null);
  const [items, setItems] = useState([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  useEffect(() => {
    const photoItems = photos.map((photo, index) => ({
      cropData: photo.cropData,
      type: photo.type,
      id: photo.id,
      itemType: 'photo',
      optionId: photo.optionId,
      photoIndex: index,
      src: photo.src,
      upholsteryName: photo.upholsteryName,
      valueId: photo.valueId,
    }));

    const videoItem = { id: introVideoId, itemType: 'video' };

    const updatedItems =
      introVideoId && photoItems.length
        ? [photoItems[0], videoItem, ...photoItems.slice(1)]
        : photoItems;

    setItems(updatedItems);
  }, [photos, introVideoId]);

  useEffect(() => {
    if (isVideoSelected) {
      const videoItemIndex = items.findIndex(item => item.itemType === 'video');

      if (videoItemIndex !== -1) {
        setSelectedItemIndex(videoItemIndex);
      }
    } else {
      const photoItemIndex = items.findIndex(
        item =>
          item.itemType === 'photo' && item.photoIndex === selectedPhotoIndex
      );

      if (photoItemIndex !== -1) {
        setSelectedItemIndex(photoItemIndex);
      }
    }
  }, [selectedPhotoIndex, items, isVideoSelected]);

  const setSelectedItemAndScroll = index => {
    setIsZoomedIn(false);

    const updatedItem = items[index];

    if (updatedItem.itemType === 'video') {
      handleVideoSelected(true);
    } else {
      handleVideoSelected(false);
      setSelectedPhotoIndex(updatedItem.photoIndex);
    }

    // If selected thumbnail ref is defined, scroll to it
    setTimeout(() => {
      const thumbnailWrapper = thumbnailWrapperRef?.current;
      const selectedThumbnail = selectedThumbnailRef?.current;

      thumbnailWrapper?.scrollTo({
        top:
          (selectedThumbnail?.offsetTop || 0) -
          (thumbnailWrapper.clientHeight || 64) / 2,
        behavior: 'smooth',
      });
    }, 10);
  };

  const numberOfItems = photos?.length ?? 0;

  useKeyboardControlForIndexedItems({
    isEnabled: isOpen,
    numberOfItems,
    selectedIndex: selectedItemIndex,
    setSelectedIndex: setSelectedItemAndScroll,
  });

  const maxPhotoWidth = usePhotoWidth();

  return (
    <div className="w-full">
      <Prefetcher onRenderAssets={photosToPreload}>
        <StandardModal
          isOpen={isOpen}
          onClose={onClose}
          theme="light"
          size="xl"
        >
          <div className="flex flex-row justify-center w-full">
            <div
              className="max-h-[calc(90vh_-_4rem)] min-w-[315px] overflow-y-auto w-[325px]"
              ref={thumbnailWrapperRef}
            >
              <div className="grid gap-[7px_7px] grid-cols-[142px_142px] mb-px min-w-[300px] w-1/4">
                {items.map(
                  ({ cropData, type, id, itemType, src }, itemIndex) => {
                    const isItemSelected = itemIndex === selectedItemIndex;

                    return (
                      <PDPDesktopFullScreenGallery2Thumbnail
                        cropData={cropData}
                        enableCrops={enableCrops}
                        isItemSelected={isItemSelected}
                        itemIndex={itemIndex}
                        itemType={itemType}
                        key={`${id}-${itemType}`}
                        selectedThumbnailRef={selectedThumbnailRef}
                        setSelectedItemAndScroll={setSelectedItemAndScroll}
                        src={src}
                        type={type}
                        videoThumbnailUrl={videoThumbnailUrl}
                      />
                    );
                  }
                )}
              </div>
            </div>

            <div className="items-center flex flex-col ml-6">
              <div
                className="items-center bg-transparent border-0 flex flex-col flex-1 justify-between max-h-[calc(90vh_-_4rem)] overflow-y-hidden"
                style={{ maxWidth: maxPhotoWidth }}
              >
                <div className="text-xl font-bold text-gray mb-4 max-w-full text-center w-[2000px]">
                  {productName}
                </div>

                <div
                  className={classNames(
                    'object-contain object-center select-none p-0 max-w-full relative flex',
                    {
                      'w-full': isVideoSelected,
                    }
                  )}
                >
                  <button
                    type="button"
                    className={classNames(
                      'items-center border-0 bottom-0 flex justify-start opacity-0 absolute top-0 transition-[opacity_.3s] w-[12%] z-[1] mx-0 my-auto focus-visible:opacity-100 focus-visible:outline-offset-[-5px] hover:opacity-100 [background:linear-gradient(90deg,rgba(0,0,0,.35)__.06%,hsla(0,0%,76.9%,0)__99.94%)] left-0  h-full',
                      {
                        hidden: isZoomedIn,
                      }
                    )}
                    onClick={() => {
                      const previousIndex = selectedItemIndex - 1;

                      // If user presses previous button, move to previous index.
                      // If at start of the list, select the last item instead.
                      if (previousIndex < 0) {
                        setSelectedItemAndScroll(numberOfItems - 1);
                      } else {
                        setSelectedItemAndScroll(previousIndex);
                      }
                    }}
                  >
                    <IconArrowRightSimple
                      width={22}
                      height={40}
                      className="ml-2.5 -rotate-180"
                      color="white"
                    />
                    <VisuallyHidden>Previous image</VisuallyHidden>
                  </button>

                  {!isVideoSelected && photoSrc?.zoomSrc ? (
                    <InnerImageZoom
                      height={600}
                      src={photoSrc.src}
                      zoomSrc={photoSrc.zoomSrc}
                      zoomPreload
                      hasSpacer
                      hideCloseButton
                      hideHint
                      afterZoomIn={() => {
                        setIsZoomedIn(true);
                      }}
                      afterZoomOut={() => {
                        setIsZoomedIn(false);
                      }}
                    />
                  ) : null}

                  {isVideoSelected && introVideoId ? (
                    <PDPProductIntroVideo
                      variant="PDPDesktopFullScreenGallery2"
                      videoId={introVideoId}
                    />
                  ) : null}

                  <button
                    type="button"
                    className={classNames(
                      'items-center border-0 bottom-0 flex opacity-0 absolute top-0 transition-[opacity_.3s] w-[12%] z-[1] mx-0 my-auto focus-visible:opacity-100 focus-visible:outline-offset-[-5px] hover:opacity-100 [background:linear-gradient(270deg,rgba(0,0,0,.35)__.06%,hsla(0,0%,76.9%,0)__99.94%)] justify-end right-0 h-full',
                      {
                        hidden: isZoomedIn,
                      }
                    )}
                    onClick={() => {
                      const nextIndex = selectedItemIndex + 1;

                      // If user presses next button, move to next index. If at
                      // end of the list, select the first item instead.
                      if (nextIndex >= numberOfItems) {
                        setSelectedItemAndScroll(0);
                      } else {
                        setSelectedItemAndScroll(nextIndex);
                      }
                    }}
                  >
                    <IconArrowRightSimple
                      width={22}
                      height={40}
                      className="mr-2.5"
                      color="white"
                    />
                    <VisuallyHidden>Next image</VisuallyHidden>
                  </button>
                </div>

                <div className="flex flex-row mt-4 w-full">
                  {!isVideoSelected && (
                    <div className="flex-[1_1] flex items-center justify-end">
                      <div className="PDPDesktopFullScreenGallery2__MainPhoto-shownin">
                        <PDPShownIn
                          fullWidth
                          isEditOrder={isEditOrder}
                          optionId={photos?.[selectedPhotoIndex]?.optionId}
                          valueId={photos?.[selectedPhotoIndex]?.valueId}
                          upholsteryName={
                            photos?.[selectedPhotoIndex]?.upholsteryName
                          }
                          onSelection={() => {
                            onClose(true);
                          }}
                          layout="greyBG"
                          showInactiveUpholstery
                          productName={productName}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </StandardModal>
      </Prefetcher>
    </div>
  );
};

PDPDesktopFullScreenGallery2UI.propTypes = {
  enableCrops: PropTypes.bool,
  handleVideoSelected: PropTypes.func,
  introVideoId: PropTypes.string,
  isEditOrder: PropTypes.bool,
  isOpen: PropTypes.bool,
  isVideoSelected: PropTypes.bool,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      cropData: PropTypes.object,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      optionId: PropTypes.number,
      src: PropTypes.string,
      type: PropTypes.oneOf([
        'buildyourown',
        'configuration',
        'dimensional',
        'studio',
      ]),
      upholsteryName: PropTypes.string,
      valueId: PropTypes.number,
    })
  ),
  photoSrc: PropTypes.shape({
    src: PropTypes.string,
    zoomSrc: PropTypes.string,
  }),
  photosToPreload: PropTypes.arrayOf(PropTypes.string),
  productName: PropTypes.string,
  selectedPhotoIndex: PropTypes.number,
  setSelectedPhotoIndex: PropTypes.func,
  videoThumbnailUrl: PropTypes.string,
};

export default PDPDesktopFullScreenGallery2UI;
